import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { organizationAPI } from '../../services/api';
import { getFileUrl } from '../../utils/fileUrl';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import { AdminPage, StateBlock } from '../Common/admin';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const OrganizationEdit = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    organizationCode: '',
    organizationNameAr: '',
    organizationNameEng: '',
    crNumber: '',
    vatNumber: '',
    countryId: '',
    areaId: '',
    cityId: '',
    districtId: '',
    streetName: '',
    telephoneNumber: '',
    mobileNumber: '',
    faxNumber: '',
    email: '',
    website: '',
    postalCode: '',
    poBox: '',
    buildingNumber: '',
    additionalBuildingNumber: '',
    zipCode: '',
    isActive: true,
  });
  const [countries, setCountries] = useState([]);
  const [areas, setAreas] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { countryId, areaId, cityId } = formData;

  useEffect(() => {
    organizationAPI
      .getCountries()
      .then((res) => setCountries(res.data || []))
      .catch((err) => console.error('Failed to load countries:', err));
  }, []);

  const loadOrganization = useCallback(async () => {
    try {
      setLoadingData(true);
      const { data } = await organizationAPI.getById(id);
      setFormData({
        organizationCode: data.organizationCode || '',
        organizationNameAr: data.organizationNameAr || '',
        organizationNameEng: data.organizationNameEng || '',
        crNumber: data.crNumber || '',
        vatNumber: data.vatNumber || '',
        countryId: data.countryId || '',
        areaId: data.areaId || '',
        cityId: data.cityId || '',
        districtId: data.districtId || '',
        streetName: data.streetName || '',
        telephoneNumber: data.telephoneNumber || '',
        mobileNumber: data.mobileNumber || '',
        faxNumber: data.faxNumber || '',
        email: data.email || '',
        website: data.website || '',
        postalCode: data.postalCode || '',
        poBox: data.poBox || '',
        buildingNumber: data.buildingNumber || '',
        additionalBuildingNumber: data.additionalBuildingNumber || '',
        zipCode: data.zipCode || '',
        isActive: !!data.isActive,
      });
      setLogoPreview(getFileUrl(data.logo));
    } catch (err) {
      setErrorMessage(t('organization.edit.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoadingData(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadOrganization();
  }, [loadOrganization]);

  // Each level filters by its parent, and clears the levels below it.
  useEffect(() => {
    if (!countryId) {
      setAreas([]);
      return;
    }
    organizationAPI
      .getAreas()
      .then((res) => {
        const filtered = (res.data || []).filter((a) => a.countryId === parseInt(countryId));
        setAreas(filtered);
        setFormData((prev) =>
          prev.areaId && !filtered.some((a) => a.id === parseInt(prev.areaId))
            ? { ...prev, areaId: '', cityId: '', districtId: '' }
            : prev
        );
      })
      .catch((err) => console.error('Failed to load areas:', err));
  }, [countryId]);

  useEffect(() => {
    if (!areaId) {
      setCities([]);
      return;
    }
    organizationAPI
      .getCities()
      .then((res) => {
        const filtered = (res.data || []).filter((c) => c.areaId === parseInt(areaId));
        setCities(filtered);
        setFormData((prev) =>
          prev.cityId && !filtered.some((c) => c.id === parseInt(prev.cityId))
            ? { ...prev, cityId: '', districtId: '' }
            : prev
        );
      })
      .catch((err) => console.error('Failed to load cities:', err));
  }, [areaId]);

  useEffect(() => {
    if (!cityId) {
      setDistricts([]);
      return;
    }
    organizationAPI
      .getDistricts()
      .then((res) => {
        const filtered = (res.data || []).filter((d) => d.cityId === parseInt(cityId));
        setDistricts(filtered);
        setFormData((prev) =>
          prev.districtId && !filtered.some((d) => d.id === parseInt(prev.districtId))
            ? { ...prev, districtId: '' }
            : prev
        );
      })
      .catch((err) => console.error('Failed to load districts:', err));
  }, [cityId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const e = {};
    [
      ['organizationCode', 'organizationCodeRequired'],
      ['organizationNameAr', 'nameArRequired'],
      ['organizationNameEng', 'nameEngRequired'],
      ['crNumber', 'crNumberRequired'],
      ['vatNumber', 'vatNumberRequired'],
      ['streetName', 'streetNameRequired'],
      ['telephoneNumber', 'telephoneNumberRequired'],
    ].forEach(([f, key]) => {
      if (!String(formData[f]).trim()) e[f] = t(`organization.edit.errors.${key}`);
    });

    [
      ['countryId', 'countryRequired'],
      ['areaId', 'areaRequired'],
      ['cityId', 'cityRequired'],
      ['districtId', 'districtRequired'],
    ].forEach(([f, key]) => {
      if (!formData[f]) e[f] = t(`organization.edit.errors.${key}`);
    });

    if (!formData.email.trim()) {
      e.email = t('organization.edit.errors.emailRequired');
    } else if (!EMAIL_RE.test(formData.email)) {
      e.email = t('organization.edit.errors.emailInvalid');
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        ...formData,
        countryId: parseInt(formData.countryId),
        areaId: parseInt(formData.areaId),
        cityId: parseInt(formData.cityId),
        districtId: parseInt(formData.districtId),
        mobileNumber: formData.mobileNumber || null,
        faxNumber: formData.faxNumber || null,
        website: formData.website || null,
        postalCode: formData.postalCode || null,
        poBox: formData.poBox || null,
        buildingNumber: formData.buildingNumber || null,
        additionalBuildingNumber: formData.additionalBuildingNumber || null,
        zipCode: formData.zipCode || null,
      };

      if (logoFile) {
        const body = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          body.append(key, value === null || value === undefined ? '' : value);
        });
        body.append('logoFile', logoFile);
        await organizationAPI.updateWithFile(id, body);
      } else {
        await organizationAPI.update(id, payload);
      }

      setShowSuccessModal(true);
      setTimeout(() => navigate('/organization'), 1000);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setErrorMessage(t('organization.edit.messages.updateError'));
        setShowErrorModal(true);
      }
      setLoading(false);
    }
  };

  const field = (name, labelKey, { type = 'text', required = false, maxLength } = {}) => (
    <div className="ad-form-field">
      <label className={`ad-label ${required ? 'ad-label-required' : ''}`} htmlFor={name}>
        {t(`organization.edit.fields.${labelKey}`)}
      </label>
      <input
        id={name}
        type={type}
        name={name}
        className={`ad-input ${errors[name] ? 'ad-input-error' : ''}`}
        value={formData[name]}
        onChange={handleChange}
        maxLength={maxLength}
      />
      {errors[name] && <span className="ad-error-text">{errors[name]}</span>}
    </div>
  );

  const select = (name, labelKey, options, optionLabel, disabled = false) => (
    <div className="ad-form-field">
      <label className="ad-label ad-label-required" htmlFor={name}>
        {t(`organization.edit.fields.${labelKey}`)}
      </label>
      <select
        id={name}
        name={name}
        className={`ad-select ${errors[name] ? 'ad-input-error' : ''}`}
        value={formData[name]}
        onChange={handleChange}
        disabled={disabled}
      >
        <option value="">{t(`organization.edit.fields.${labelKey}`)}</option>
        {options.map((item) => (
          <option key={item.id} value={item.id}>
            {optionLabel(item)}
          </option>
        ))}
      </select>
      {errors[name] && <span className="ad-error-text">{errors[name]}</span>}
    </div>
  );

  const backButton = (
    <button type="button" className="ad-btn" onClick={() => navigate('/organization')}>
      <i className="fas fa-arrow-right"></i>
      {t('organization.edit.backToList')}
    </button>
  );

  if (loadingData) {
    return (
      <AdminPage title={t('organization.edit.title')} headerActions={backButton}>
        <StateBlock icon="fa-hourglass-half" title={t('common.loading')} />
      </AdminPage>
    );
  }

  return (
    <AdminPage title={t('organization.edit.title')} headerActions={backButton}>
      <form onSubmit={handleSubmit}>
        <div className="ad-form">
          <div className="ad-form-grid">
            {field('organizationCode', 'organizationCode', { required: true, maxLength: 20 })}
            {field('organizationNameAr', 'nameAr', { required: true, maxLength: 200 })}
            {field('organizationNameEng', 'nameEng', { required: true, maxLength: 200 })}
            {field('crNumber', 'crNumber', { required: true, maxLength: 50 })}
            {field('vatNumber', 'vatNumber', { required: true, maxLength: 50 })}
          </div>

          <div className="ad-form-section">
            <h2 className="ad-form-section-title">
              {t('organization.edit.fields.streetName')}
            </h2>
            <div className="ad-form-grid">
              {select('countryId', 'country', countries, (c) => c.nameEng)}
              {select('areaId', 'area', areas, (a) => a.areaNameEng, !countryId)}
              {select('cityId', 'city', cities, (c) => c.cityNameEng, !areaId)}
              {select('districtId', 'district', districts, (d) => d.districtNameEng, !cityId)}
              {field('streetName', 'streetName', { required: true, maxLength: 200 })}
              {field('buildingNumber', 'buildingNumber', { maxLength: 20 })}
              {field('additionalBuildingNumber', 'additionalBuildingNumber', { maxLength: 20 })}
              {field('postalCode', 'postalCode', { maxLength: 20 })}
              {field('poBox', 'poBox', { maxLength: 20 })}
              {field('zipCode', 'zipCode', { maxLength: 20 })}
            </div>
          </div>

          <div className="ad-form-section">
            <h2 className="ad-form-section-title">{t('organization.edit.fields.email')}</h2>
            <div className="ad-form-grid">
              {field('telephoneNumber', 'telephoneNumber', { required: true, maxLength: 20 })}
              {field('mobileNumber', 'mobileNumber', { maxLength: 20 })}
              {field('faxNumber', 'faxNumber', { maxLength: 20 })}
              {field('email', 'email', { type: 'email', required: true, maxLength: 100 })}
              {field('website', 'website', { maxLength: 200 })}
            </div>
          </div>

          <div className="ad-form-section">
            <h2 className="ad-form-section-title">{t('organization.edit.fields.logo')}</h2>
            <div className="ad-file-row">
              <span className="ad-file-preview">
                {logoPreview ? <img src={logoPreview} alt="" /> : <i className="fas fa-image"></i>}
              </span>
              <input
                type="file"
                accept="image/*"
                className="ad-input"
                onChange={handleLogoChange}
              />
            </div>
          </div>

          <div className="ad-form-section">
            <div className="ad-check-row">
              <input
                id="isActive"
                type="checkbox"
                name="isActive"
                className="ad-checkbox"
                checked={formData.isActive}
                onChange={handleChange}
              />
              <label htmlFor="isActive">{t('organization.edit.fields.isActive')}</label>
            </div>
          </div>
        </div>

        <div className="ad-form-foot">
          <button type="button" className="ad-btn" onClick={() => navigate('/organization')}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="ad-btn ad-btn-primary" disabled={loading}>
            {loading ? t('common.loading') : t('organization.edit.save')}
          </button>
        </div>
      </form>

      <SuccessModal
        show={showSuccessModal}
        message={t('organization.edit.messages.updateSuccess')}
        onClose={() => setShowSuccessModal(false)}
      />
      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </AdminPage>
  );
};

export default OrganizationEdit;
